import qs from 'qs';
import { formatRedirect } from '@/search-for-retail/redirect/redirect.formatters';
import { parseRedirect } from '@/search-for-retail/redirect/redirect.parsers';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the redirect resource under an area. */
const REDIRECT_PATH = 'redirect';
/**
 * RTK Query endpoints for redirect data.
 *
 * This module extends the base ccApi with redirect query, mutation, and
 * promotion endpoints.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const redirectApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the list of redirects for a given area.
         *
         * @endpoint GET /{areaId}/redirect
         * @returns Parsed Redirect[] via transformResponse
         */
        getRedirectList: builder.query({
            query: ({ areaId }) => `/${areaId}/${REDIRECT_PATH}`,
            transformResponse: (response) => response.map(parseRedirect),
            providesTags: [CC_API_TAGS.RedirectList],
        }),
        /**
         * Fetches a single redirect by ID within an area.
         *
         * @endpoint GET /{areaId}/redirect/{id}
         * @returns Parsed Redirect via transformResponse
         */
        getRedirectById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${REDIRECT_PATH}/${id}`,
            transformResponse: (response) => parseRedirect(response),
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.Redirect, id }],
        }),
        /**
         * Creates a new redirect in the given area.
         *
         * @endpoint POST /{areaId}/redirect
         * @returns Parsed Redirect via transformResponse
         */
        createRedirect: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${REDIRECT_PATH}`,
                method: 'POST',
                body: formatRedirect(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseRedirect(response),
            invalidatesTags: [CC_API_TAGS.RedirectList],
        }),
        /**
         * Updates an existing redirect.
         *
         * @endpoint PUT /{areaId}/redirect/{id}
         * @returns Parsed Redirect via transformResponse
         */
        updateRedirect: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${REDIRECT_PATH}/${data.id}`,
                method: 'PUT',
                body: formatRedirect(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseRedirect(response),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.RedirectList,
                { type: CC_API_TAGS.Redirect, id: data.id },
            ],
        }),
        /**
         * Deletes a single redirect from an area.
         *
         * @endpoint DELETE /{areaId}/redirect/{id}
         */
        deleteRedirect: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${areaId}/${REDIRECT_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.RedirectList,
                { type: CC_API_TAGS.Redirect, id },
            ],
        }),
        /**
         * Bulk-deletes redirects from an area.
         *
         * @endpoint DELETE /{areaId}/redirect/bulk/delete
         * @returns The list of successfully deleted redirects
         */
        bulkDeleteRedirect: builder.mutation({
            query: ({ areaId, ids }) => {
                const query = qs.stringify({ ids }, { indices: false });
                return {
                    url: `/${areaId}/${REDIRECT_PATH}/bulk/delete?${query}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.RedirectList],
        }),
        /**
         * Promotes a redirect to another area.
         *
         * @endpoint POST /{areaId}/redirect/{id}/promote/{targetAreaId}
         */
        promoteRedirectToArea: builder.mutation({
            query: ({ areaId, id, targetAreaId }) => ({
                url: `/${areaId}/${REDIRECT_PATH}/${id}/promote/${targetAreaId}`,
                method: 'POST',
            }),
            invalidatesTags: [CC_API_TAGS.RedirectList],
        }),
        /**
         * Bulk-promotes redirects to another area.
         *
         * @endpoint POST /{areaId}/redirect/bulk/promote/{targetAreaId}
         */
        bulkPromoteRedirectToArea: builder.mutation({
            query: ({ areaId, targetAreaId, ids }) => {
                const query = qs.stringify({ ids }, { indices: false });
                return {
                    url: `/${areaId}/${REDIRECT_PATH}/bulk/promote/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.RedirectList],
        }),
    }),
});
export const { useBulkDeleteRedirectMutation, useBulkPromoteRedirectToAreaMutation, useCreateRedirectMutation, useDeleteRedirectMutation, useGetRedirectByIdQuery, useGetRedirectListQuery, useLazyGetRedirectListQuery, usePromoteRedirectToAreaMutation, useUpdateRedirectMutation, } = redirectApi;
